import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { getMe, login as loginApi, logout as logoutApi } from '../api/authApi';

const AuthContext = createContext(null);

function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('auth_token', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('auth_token');
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore token from localStorage on mount and verify it
  useEffect(() => {
    const saved = localStorage.getItem('auth_token');
    if (saved) {
      setAuthToken(saved);
      getMe()
        .then(res => setUser(res.data))
        .catch(() => {
          setAuthToken(null);
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await getMe();
      setUser(res.data);
    } catch {
      setUser(null);
      setAuthToken(null);
    }
  }, []);

  const login = async (email, password) => {
    const res = await loginApi(email, password);
    const token = res.data.access_token;
    setAuthToken(token);
    await checkAuth();
    return res;
  };

  const logout = async () => {
    try {
      await logoutApi();
    } catch { /* ignore */ }
    setAuthToken(null);
    setUser(null);
  };

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated, isAdmin, loading, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
